<template>
  <v-card v-if="game.id" class="feeds-list mb-0" elevation="1">
    <v-card-title class="d-flex align-center">
      <v-icon class="mr-2">mdi-rss</v-icon>
      <span class="font-weight-bold">Associated Feeds</span>
      <v-spacer />
      <v-btn
        class="mr-2"
        color="error"
        outlined
        data-testid="associated-feeds-remove-selected"
        @click="onRemoveManyFeeds"
      >
        Remove selected feeds
      </v-btn>
      <v-dialog v-model="addFeed.dialog" max-width="800" persistent>
        <template v-slot:activator="{ on, attrs }">
          <v-btn
            color="success"
            data-testid="associated-feeds-add-btn"
            v-bind="attrs"
            v-on="on"
          >
            Add
          </v-btn>
        </template>
        <v-card class="admin-dialog">
          <v-card-title class="d-flex align-center admin-dialog__title">
            <span class="font-weight-bold">Add {{ game.name }} game to feeds</span>
            <v-spacer />
            <v-btn icon small @click="closeAddDialog">
              <v-icon>mdi-close</v-icon>
            </v-btn>
          </v-card-title>
          <v-card-text class="admin-dialog__body">
            <v-row dense class="mb-3">
              <v-col cols="12" md="6">
                <v-select
                  v-model="addFeed.gameVersionId"
                  label="Version"
                  :items="versionOptions"
                  item-text="version"
                  item-value="id"
                  dense
                  outlined
                  hide-details
                  data-testid="associated-feeds-dialog-version"
                />
              </v-col>
              <v-col cols="12" md="6">
                <v-text-field
                  v-model="addFeed.search"
                  label="Search feeds"
                  dense
                  outlined
                  hide-details
                  class="mt-0"
                  prepend-inner-icon="mdi-magnify"
                />
              </v-col>
            </v-row>
            <v-data-table
              class="admin-dialog__table"
              dense
              sort-by="id"
              show-select
              :search="addFeed.search"
              :headers="addFeed.TableHeaders"
              :items="getAbsentFeeds()"
              v-model="addFeed.selected"
              :items-per-page="10"
              :footer-props="{ itemsPerPageOptions: [10, 25, 50, -1] }"
            />
          </v-card-text>
          <v-card-actions class="admin-dialog__actions">
            <v-spacer />
            <v-btn
              color="primary"
              class="mr-2"
              data-testid="associated-feeds-dialog-save"
              :disabled="!addFeed.selected.length"
              @click="addFeeds()"
            >
              Save
            </v-btn>
            <v-btn outlined @click="closeAddDialog">Cancel</v-btn>
          </v-card-actions>
        </v-card>
      </v-dialog>
    </v-card-title>
    <v-card-text class="feeds-list__search py-1">
      <v-text-field
        v-model="feedSearch"
        prepend-icon="mdi-magnify"
        label="Search"
        dense
        hide-details
        class="mt-0"
      />
    </v-card-text>
    <v-data-table
      class="feeds-list__table"
      :loading="!game"
      :search="feedSearch"
      :headers="feedGamesTableHeaders"
      :items="game.feedGames"
      sort-by="feed.id"
      show-select
      v-model="feedTable.selected"
      :items-per-page="10"
      :footer-props="{ itemsPerPageOptions: [10, 25, 50, -1] }"
    >
      <template v-slot:item.version="{ item }">
        <v-select
          v-model="item.gameVersionId"
          :items="game.versions.concat([{ id: null, version: 'Latest' }])"
          item-text="version"
          item-value="id"
          dense
          hide-details
          class="mt-0"
          :data-testid="`associated-feeds-version-select-${item.feedId}`"
          @change="onVersionChange(item)"
        />
      </template>
      <template v-slot:item.action="{item}">
        <div class="feeds-list__actions">
          <v-tooltip v-if="item.id" bottom>
            <template v-slot:activator="{ on, attrs }">
              <v-icon
                color="secondary"
                tag="a"
                :href="changeLogHref(item)"
                target="_blank"
                rel="noopener noreferrer"
                :data-testid="`associated-feeds-history-${item.id}`"
                v-bind="attrs"
                v-on="on"
              >
                mdi-history
              </v-icon>
            </template>
            <span>View change history</span>
          </v-tooltip>
          <v-tooltip bottom>
            <template v-slot:activator="{ on, attrs }">
              <v-icon
                color="warning"
                v-bind="attrs"
                v-on="on"
                :data-testid="`associated-feeds-delete-${item.feedId}`"
                @click="deleteItem(item)"
              >
                mdi-delete
              </v-icon>
            </template>
            <span>Remove from feed</span>
          </v-tooltip>
        </div>
      </template>
    </v-data-table>
  </v-card>
</template>

<script>
import persistentDialogEsc from '@/mixins/persistentDialogEsc';

export default {
  name: 'FeedsList',
  components: {},
  mixins: [
    persistentDialogEsc({
      'addFeed.dialog': 'closeAddDialog',
    }),
  ],
  props: {
    game: {},
    items: [],
    feeds: []
  },
  data() {
    return {
      feedSearch: '',
      addFeed: {
        dialog: false,
        search: "",
        selected: [],
        gameVersionId: null,
        TableHeaders: [
          {text: 'Id', value: 'id'},
          {text: 'Name', value: 'name'},
          {text: 'Environment', value: 'environment'},
        ]
      },
      feedTable: {
        selected: []
      },
      feedGamesTableHeaders: [
        {text: 'Id', value: 'feed.id', width: '72px'},
        {text: 'Name', value: 'feed.name', width: '28%'},
        {text: 'Environment', value: 'feed.environment', width: '220px'},
        {text: 'Version', value: 'version', width: '240px'},
        {
          text: 'Actions',
          value: 'action',
          sortable: false,
          align: 'center',
          width: '1%',
          class: 'feeds-list__col--compact',
          cellClass: 'feeds-list__col--compact',
        },
      ],
    };
  },
  computed: {
    versionOptions() {
      return [{ id: null, version: 'Latest' }].concat(this.game.versions || []);
    }
  },
  watch: {
    'addFeed.dialog'(open) {
      if (open) {
        this.addFeed.search = '';
        this.addFeed.selected = [];
        this.addFeed.gameVersionId = null;
      }
    }
  },
  methods: {
    changeLogLink(item) {
      return {
        path: '/game-data-change-log',
        query: {
          table: 'feed_game',
          entityId: String(item.id),
        },
      };
    },
    changeLogHref(item) {
      return this.$router.resolve(this.changeLogLink(item)).href;
    },
    getAbsentFeeds() {
      return this.feeds.filter(feed => !this.game.feedGames?.some(fg => fg.feedId === feed.id)).map(i => i);
    },
    closeAddDialog() {
      this.addFeed.dialog = false;
    },
    async addFeeds() {
      await Promise.all(
        this.addFeed.selected.map(feed => this.$gameApi.post(`/feeds/${feed.id}/games`, {
          gameId: this.game.id,
          feedId: feed.id,
          gameVersionId: this.addFeed.gameVersionId
        }))
      );
      this.closeAddDialog();
      this.addFeed.selected = [];
      this.addFeed.gameVersionId = null;
      this.$emit('update');
    },
    async deleteItem(item) {
      const {isConfirmed} = await this.$swal.fire({
        icon: 'warning',
        title: 'This will delete all feed information. Are you sure to proceed?',
        showCancelButton: true
      });
      if (isConfirmed) {
        await this.$gameApi.delete(`/feeds/${item.feedId}/games/${this.game.id}`);

        this.$emit('update');
      }
    },
    async onVersionChange(item) {
      try {
        await this.$gameApi.patch(`/feeds/${item.feedId}/games/${this.game.id}`, item)
        this.$emit('update')
      } catch (e) {
        await this.$swal.fire({
          icon: 'error',
          title: `${e.statusCode} - Error for ${e.path}`,
          text: e.details,
          timer: 1500,
          showConfirmButton: false
        });
      }
    },
    async onRemoveManyFeeds() {
      const {isConfirmed} = await this.$swal.fire({
        icon: 'warning',
        title: 'This will delete game from all feeds. Are you sure to proceed?',
        showCancelButton: true
      });
      if (isConfirmed) {
        try {
          await Promise.all(
            this.feedTable.selected.map(feed => this.$gameApi.delete(`/feeds/${feed.feedId}/games/${feed.gameId}`))
          );
          this.$emit('update')
        } catch (e) {
          await this.$swal.fire({
            icon: 'error',
            title: `${e.statusCode} - Error for ${e.path}`,
            text: e.details,
            timer: 1500,
            showConfirmButton: false
          });
        }
      }
    }
  }
};
</script>

<style scoped lang="scss">
.feeds-list {
  overflow: hidden;
}

.feeds-list__actions {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  white-space: nowrap;
}

.feeds-list__table ::v-deep .v-data-table__wrapper {
  overflow-x: auto;
}

.feeds-list__table ::v-deep tbody td {
  white-space: nowrap;
  vertical-align: middle;
}

.feeds-list__table ::v-deep .v-select {
  max-width: 220px;
  width: 100%;
}

.feeds-list__table ::v-deep .feeds-list__col--compact {
  white-space: nowrap;
  width: 1%;
  padding-left: 8px;
  padding-right: 8px;
}
</style>
